import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Refresh,
  Add,
  Payments,
  Drafts,
  PendingActions,
  CheckCircle,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';
import { hrService, PayrollRun, PayrollRunStatus } from '../../services/hrService';

const statusTone: Record<PayrollRunStatus, StatusTone> = {
  DRAFT: 'neutral',
  PROCESSING: 'info',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'success',
  PAID: 'success',
  CANCELLED: 'error',
};

const statusLabel: Record<PayrollRunStatus, string> = {
  DRAFT: 'Draft',
  PROCESSING: 'Processing',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  PAID: 'Paid',
  CANCELLED: 'Cancelled',
};

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const money = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n === null ? '—' : formatIndianNumber(n);
};

const PayrollPage: React.FC = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<PayrollRunStatus | 'ALL'>('ALL');

  const [createOpen, setCreateOpen] = useState(false);
  const [createMonth, setCreateMonth] = useState(new Date().getMonth() + 1);
  const [createYear, setCreateYear] = useState(new Date().getFullYear());
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadRuns = () => {
    setLoading(true);
    setError(null);
    hrService
      .getPayrollRuns()
      .then((res) => setRuns(res.payrollRuns))
      .catch((err: any) => setError(err.message || 'Failed to load payroll runs'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadRuns();
  }, []);

  const counts = useMemo(() => {
    const c: Record<PayrollRunStatus, number> = {
      DRAFT: 0,
      PROCESSING: 0,
      PENDING_APPROVAL: 0,
      APPROVED: 0,
      PAID: 0,
      CANCELLED: 0,
    };
    runs.forEach((r) => {
      c[r.status] += 1;
    });
    return c;
  }, [runs]);

  const totalNetPaid = useMemo(
    () => runs.filter((r) => r.status === 'PAID').reduce((sum, r) => sum + (toNum(r.net_total) ?? 0), 0),
    [runs]
  );

  const filteredRuns = statusFilter === 'ALL' ? runs : runs.filter((r) => r.status === statusFilter);

  const openCreate = () => {
    setCreateMonth(new Date().getMonth() + 1);
    setCreateYear(new Date().getFullYear());
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await hrService.createPayrollRun({ periodMonth: createMonth, periodYear: createYear });
      setCreateOpen(false);
      loadRuns();
      // Jump straight into the new run's cockpit if the API returns its id.
      const newId = (created as any)?.payrollRun?.id ?? (created as any)?.id;
      if (newId) navigate(`/hr/payroll/${newId}`);
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create payroll run');
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Payroll"
        subtitle="Monthly runs, payslip generation, statutory summary and the GL posting lifecycle"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton onClick={loadRuns} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openCreate}>
              New Payroll Run
            </Button>
          </>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Draft Runs" value={counts.DRAFT} icon={<Drafts />} color="#64748b" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Pending Approval" value={counts.PENDING_APPROVAL} icon={<PendingActions />} color="#d97706" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Paid Runs" value={counts.PAID} icon={<CheckCircle />} color="#059669" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Total Net Paid" value={formatIndianNumber(totalNetPaid)} icon={<Payments />} color="#7c3aed" />
        </Grid>
      </Grid>

      <FilterBar>
        <Chip label={`All (${runs.length})`} color={statusFilter === 'ALL' ? 'primary' : 'default'} variant={statusFilter === 'ALL' ? 'filled' : 'outlined'} onClick={() => setStatusFilter('ALL')} size="small" />
        {(Object.keys(statusLabel) as PayrollRunStatus[]).map((s) => (
          <Chip
            key={s}
            label={`${statusLabel[s]} (${counts[s]})`}
            color={statusFilter === s ? 'primary' : 'default'}
            variant={statusFilter === s ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(s)}
            size="small"
          />
        ))}
        <FilterSpacer />
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredRuns.length}</strong> runs
        </Typography>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Run', 'Period', 'Status', 'Employees', 'Gross', 'Deductions', 'Net'].map((h, i) => (
                  <TableCell key={h} align={i >= 3 ? 'right' : 'left'} sx={headerCellSx}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filteredRuns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState title="No payroll runs match this filter" hint="Create a new run or clear the status filter." />
                  </TableCell>
                </TableRow>
              ) : (
                filteredRuns.map((r) => (
                  <TableRow key={r.id} sx={{ ...bodyRowSx, cursor: 'pointer' }} onClick={() => navigate(`/hr/payroll/${r.id}`)}>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                        {r.run_code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {monthNames[r.period_month - 1]} {r.period_year}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusDot label={statusLabel[r.status]} tone={statusTone[r.status]} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {r.employee_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(r.gross_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(r.deduction_total)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {money(r.net_total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      <Dialog open={createOpen} onClose={() => (createBusy ? undefined : setCreateOpen(false))} maxWidth="xs" fullWidth>
        <DialogTitle>New Payroll Run</DialogTitle>
        <DialogContent dividers>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Month"
              value={createMonth}
              onChange={(e) => setCreateMonth(Number(e.target.value))}
            >
              {monthNames.map((m, i) => (
                <MenuItem key={m} value={i + 1}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              label="Year"
              type="number"
              value={createYear}
              onChange={(e) => setCreateYear(Number(e.target.value))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createBusy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={createBusy}>
            {createBusy ? 'Creating…' : 'Create Draft Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollPage;
